import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { GiFlowerPot, GiMountains, GiTreeBranch } from 'react-icons/gi';
import { FaHome } from 'react-icons/fa';
import ABOUT_NAVYA from '../assets/ABOUT NAVYA.jpg';

const About = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const features = [
    { icon: <FaHome />, text: "Clean & Spacious Rooms" },
    { icon: <GiTreeBranch />, text: "Well-Maintained Surroundings" },
    { icon: <GiFlowerPot />, text: "Green Terrace Gardens" },
    { icon: <GiMountains />, text: "Multiple Stay Options" },
  ];

  return (
    <section id="about" className="section-padding bg-gradient-to-b from-white to-primary-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            className="inline-block mb-4"
          >
            <GiTreeBranch className="text-primary-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About <span className="font-script text-primary-600">Navyaa</span>
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The Home Stay
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={ABOUT_NAVYA}
                  alt="Navyaa Homestay"
                  className="w-full h-[500px] object-cover"
                />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <p className="text-2xl font-bold">Since 2024</p>
                <p className="text-sm">Serving with Love</p>
              </div>
            </div>
            
            {/* Floating Card */}
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl"
            >
              <p className="text-3xl font-bold text-primary-600">200+</p>
              <p className="text-sm text-gray-600">Happy Guests</p>
            </motion.div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Welcome To Your Comfort Zone
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Hosted by the welcoming couple, Rajesh & Archana Mohite, Navyaa – The Homestay is built on the belief that true comfort is found in the feeling of home. Located in the heart of Laxmi Nagar, Nagpur, our thoughtfully designed service apartments blend warmth with attentive hospitality, creating a truly comfortable stay for every guest.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Whether you are hosting wedding guests, arranging accommodation for corporate employees, accompanying a loved one during recovery, or planning a family vacation, Navyaa – The Homestay offers an ideal stay solution. With multiple service apartment options, we cater to diverse needs while ensuring flexibility, convenience, and a homely experience.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="text-primary-500 text-3xl">{feature.icon}</div>
                  <span className="text-gray-700 font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </div>

            <a href="#booking" className="btn-primary inline-block">
              Book Your Stay
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
